// Meta (Facebook/Instagram) Pixel. Loaded ONLY where a pixel ID is configured
// (paid-ad traffic lands on production), so dev/staging never pull the third-party
// script. Mirrors x-pixel.ts / firebase.ts: conditional init keyed on an env var,
// plus the official fbq queue stub so conversions fired before fbevents.js loads
// aren't dropped.
//
// GA4 remains the master analytics dashboard (campaign funnel is read there via
// UTM + existing events). This pixel exists purely so Meta Ads Manager can
// attribute signups/activations to ad spend and optimize delivery. Server-side
// Conversions API (backend/shared/src/analytics/meta_capi.rs) recovers the
// browser events lost to ad blockers / iOS, deduped against the browser pixel by
// a shared `eventID`.

const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID || '';

// Only ever load on the live site. Guards against a local prod-preview (which
// inherits .env.production) firing real test conversions into Meta Ads.
const LIVE_HOST = 'polymyth.quest';

type Fbq = {
  (...args: unknown[]): void;
  // Set by fbevents.js once it loads; the stub delegates to it so calls made
  // AFTER load actually execute instead of piling up in `queue` forever.
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

let initialized = false;

export function isMetaPixelEnabled(): boolean {
  return Boolean(META_PIXEL_ID);
}

export function initMetaPixel(): void {
  if (
    initialized ||
    !META_PIXEL_ID ||
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  )
    return;
  if (window.location.hostname !== LIVE_HOST) return;
  initialized = true;

  if (!window.fbq) {
    const fbq: Fbq = function (...args: unknown[]) {
      // Once fbevents.js installs the real dispatcher it sets callMethod; until
      // then, buffer calls. Matches Meta's official stub so post-load events fire.
      if (fbq.callMethod) fbq.callMethod(...args);
      else (fbq.queue = fbq.queue || []).push(args);
    };
    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const first = document.getElementsByTagName('script')[0];
    first?.parentNode?.insertBefore(script, first);
  }

  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
}
